import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Check, X, CheckCheck, Loader2, Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineLoading } from "@/components/common/Loading";
import { Money } from "@/components/common/Money";
import { supabase, callFunction } from "@/lib/supabase";
import { findOperator } from "@/lib/constants";
import { formatRelative, formatPhone, formatFcfa } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { TransactionRow, UserRow } from "@/types/database";

type WithdrawalRow = TransactionRow & {
  users: Pick<UserRow, "first_name" | "phone" | "email"> | null;
};

type StatusFilter = "pending" | "success" | "failed" | "other";

const FILTER_LABELS: Record<StatusFilter, string> = {
  pending: "En attente",
  success: "Validés",
  failed: "Échoués",
  other: "Autres",
};

// "failed" et "refunded" sont regroupés : un retrait remboursé est un
// retrait qui n'a pas abouti côté mobile money (numéro invalide, rejet
// admin, échec disburse...), donc "échoué" du point de vue de l'admin.
const FAILED_STATUSES = ["failed", "refunded"];
const KNOWN_STATUSES = ["pending", "success", ...FAILED_STATUSES];

const STATUS_BADGE: Record<string, { variant: "warning" | "success" | "destructive" | "secondary"; label: string }> = {
  pending: { variant: "warning", label: "En attente" },
  processing: { variant: "secondary", label: "En cours" },
  success: { variant: "success", label: "Validé" },
  failed: { variant: "destructive", label: "Échoué" },
  refunded: { variant: "destructive", label: "Remboursé" },
};

function useWithdrawals(filter: StatusFilter) {
  return useQuery({
    queryKey: ["admin-withdrawals", filter],
    refetchInterval: 30_000,
    queryFn: async (): Promise<WithdrawalRow[]> => {
      let q = supabase
        .from("transactions")
        .select("*, users(first_name, phone, email)")
        .eq("type", "withdraw")
        .order("created_at", { ascending: false })
        .limit(200);
      if (filter === "pending") q = q.eq("status", "pending");
      else if (filter === "success") q = q.eq("status", "success");
      else if (filter === "failed") q = q.in("status", FAILED_STATUSES);
      // "other" = tout ce qui n'est pas pending/success/failed/refunded
      // (aujourd'hui : "processing") — pas de liste figée, pour rester
      // correct si un nouveau statut apparaît plus tard.
      else q = q.not("status", "in", `(${KNOWN_STATUSES.join(",")})`);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as WithdrawalRow[];
    },
  });
}

export function AdminWithdrawals() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>("pending");
  const [search, setSearch] = useState("");
  const { data, isLoading } = useWithdrawals(filter);
  const [massProgress, setMassProgress] = useState<{ done: number; total: number } | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data ?? [];
    return (data ?? []).filter((tx) => {
      const haystack = [
        tx.users?.first_name,
        tx.users?.email,
        tx.users?.phone,
        tx.phone_number,
        tx.operator,
        formatFcfa(tx.amount),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data, search]);

  const approve = useMutation({
    mutationFn: (txId: string) =>
      callFunction("paydunya-admin-payout", { transactionId: txId, action: "approve" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-withdrawals"] });
      toast.success("Retrait validé");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Erreur"),
  });

  const reject = useMutation({
    mutationFn: (txId: string) =>
      callFunction("paydunya-admin-payout", { transactionId: txId, action: "reject" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-withdrawals"] });
      toast.success("Retrait rejeté et remboursé");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Erreur"),
  });

  const count = filtered.length;
  const totalAmount = filtered.reduce((s, t) => s + t.amount, 0);

  async function massValidate(items: WithdrawalRow[]) {
    setMassProgress({ done: 0, total: items.length });
    let okCount = 0;
    let failCount = 0;
    for (let i = 0; i < items.length; i++) {
      const tx = items[i];
      try {
        await callFunction("paydunya-admin-payout", { transactionId: tx.id, action: "approve" });
        okCount++;
      } catch {
        failCount++;
      }
      setMassProgress({ done: i + 1, total: items.length });
    }
    setMassProgress(null);
    qc.invalidateQueries({ queryKey: ["admin-withdrawals"] });
    if (failCount === 0) {
      toast.success(`${okCount} retrait${okCount > 1 ? "s" : ""} validé${okCount > 1 ? "s" : ""}`);
    } else {
      toast.warning(`${okCount} validés · ${failCount} échec${failCount > 1 ? "s" : ""}`);
    }
  }

  return (
    <div className="space-y-4">
      {/* Filtres status */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {(Object.keys(FILTER_LABELS) as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={cn(
                "inline-flex items-center h-8 px-3 text-xs font-medium rounded-md transition-colors border",
                filter === s
                  ? "bg-primary/15 text-primary border-primary/30"
                  : "bg-white/[0.03] text-muted-foreground border-white/[0.06] hover:text-foreground hover:bg-white/[0.06]"
              )}
            >
              {FILTER_LABELS[s]}
            </button>
          ))}
        </div>
        {filter === "pending" && count > 0 && (
          <Button
            size="sm"
            variant="default"
            onClick={() => massValidate(filtered)}
            disabled={!!massProgress}
          >
            {massProgress ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {massProgress.done} / {massProgress.total}
              </>
            ) : (
              <>
                <CheckCheck className="size-4" /> Valider tout
              </>
            )}
          </Button>
        )}
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="Email, téléphone, opérateur, montant…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-10 bg-white/[0.03] border-white/[0.06] rounded-lg"
        />
      </div>

      {/* Sommaire */}
      {count > 0 && (
        <div className="flex items-center justify-between text-xs">
          <p className="text-muted-foreground">
            {count} retrait{count > 1 ? "s" : ""} · {FILTER_LABELS[filter].toLowerCase()}
          </p>
          <p className="text-muted-foreground">
            Total : <strong className="text-foreground"><Money amount={totalAmount} /></strong>
          </p>
        </div>
      )}

      {isLoading ? (
        <InlineLoading />
      ) : count === 0 ? (
        <EmptyState title={`Aucun retrait ${FILTER_LABELS[filter].toLowerCase()}`} />
      ) : (
        <div className="space-y-2">
          {filtered.map((tx) => {
            const md = (tx.metadata ?? {}) as Record<string, unknown>;
            const fee = typeof md.fee === "number" ? (md.fee as number) : 0;
            const gross = typeof md.gross_amount === "number" ? (md.gross_amount as number) : tx.amount;
            const badge = STATUS_BADGE[tx.status] ?? { variant: "secondary" as const, label: t(`wallet.txStatus.${tx.status}`, tx.status) };
            const isPending = tx.status === "pending";
            return (
              <Card key={tx.id}>
                <CardContent className="py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{tx.users?.first_name ?? "—"}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {tx.users?.phone ? formatPhone(tx.users.phone) : tx.users?.email ?? "—"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {tx.operator && (findOperator(tx.operator)?.name ?? tx.operator)}
                      {" · "}
                      {tx.phone_number && formatPhone(tx.phone_number)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatRelative(tx.created_at, i18n.language as "fr" | "en")}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <Money amount={tx.amount} className="font-bold text-lg block num" />
                    {fee > 0 && (
                      <p className="text-2xs text-muted-foreground">
                        + {formatFcfa(fee)} frais · total {formatFcfa(gross)}
                      </p>
                    )}
                    <Badge variant={badge.variant} className="mt-1">
                      {badge.label}
                    </Badge>
                  </div>
                  {isPending && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="default"
                        loading={approve.isPending && approve.variables === tx.id}
                        disabled={
                          (approve.isPending && approve.variables !== tx.id) ||
                          (reject.isPending && reject.variables === tx.id)
                        }
                        onClick={() => approve.mutate(tx.id)}
                      >
                        <Check /> Valider
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        loading={reject.isPending && reject.variables === tx.id}
                        disabled={
                          (reject.isPending && reject.variables !== tx.id) ||
                          (approve.isPending && approve.variables === tx.id)
                        }
                        onClick={() => reject.mutate(tx.id)}
                      >
                        <X /> Rejeter
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
